import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, signal, ViewChild, ElementRef, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { MediaObjectFit, MediaSource } from '@newroom-connect/library/types';

import { IconComponent } from '../icon/icon.component';
import { LoadingSpinnerComponent } from '../loading/loading-spinner/loading-spinner.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    NgOptimizedImage,
    LoadingSpinnerComponent
  ],
  selector: 'nrc-image',
  templateUrl: './image.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./image.component.scss']
})
export class ImageComponent implements AfterViewInit {
  // ViewChild reference to the main image element
  @ViewChild('mainImage', { static: false }) private mainImageRef!: ElementRef<HTMLImageElement>;

  // ViewChild reference to the thumbnail image element
  @ViewChild('thumbnailImage', { static: false }) private thumbnailImageRef!: ElementRef<HTMLImageElement>;

  @Input({ required: true }) public source!: MediaSource;
  @Input() public thumbnail?: MediaSource;
  @Input() public alt?: string;
  @Input() public objectFit: MediaObjectFit = 'cover';
  @Input() public isTransparent = false;
  @Input() public isRounded = false;
  @Input() public priority = true;

  @Output() public loadComplete = new EventEmitter<void>();

  public isLoaded = signal<boolean>(false);
  public hasError = signal<boolean>(false);
  public isThumbnailLoaded = signal<boolean>(false);
  public hasThumbnailError = signal<boolean>(false);

  /**
   * Called after the view initializes. Checks if the main image is already cached and loaded,
   * otherwise sets up an `onload` handler to toggle visibility.
   */
  public ngAfterViewInit(): void {
    const image = this.mainImageRef?.nativeElement;
    const thumbnailImage = this.thumbnailImageRef?.nativeElement;

    // Handle main image loading state
    // Surround with setTimeout for angular to detect changes in the next cycle run.
    setTimeout(() => {
      if (image) {
        this.setUpLoadEvent(image, () => {
          this.isLoaded.set(true);
          this.loadComplete.emit();
        });
      }

      // Handle thumbnail loading state
      if (thumbnailImage) {
        this.setUpLoadEvent(thumbnailImage, () => {
          this.isThumbnailLoaded.set(true);
        });
      }
    }, 100);
  }

  /**
   * Handle image load error.
   */
  public onError(): void {
    this.hasError.set(true);
    this.isLoaded.set(false);
  }

  /**
   * Handle thumbnail load error.
   */
  public onThumbnailError(): void {
    this.hasThumbnailError.set(true);
    this.isThumbnailLoaded.set(false);
  }

  /**
   * Handle load event for a given image.
   *
   * @param image Image element referece.
   * @param callback Callback function to call when the image is loaded.
   */
  private setUpLoadEvent(image: HTMLImageElement, callback: () => void): void {
    // Check if image is already loaded (cached or completed)
    const isImageLoaded = image.complete || image.naturalWidth > 0;

    if (isImageLoaded && !this.isLoaded()) {
      callback();

      return;
    }

    image.onload = callback;
  }
}
